import React, { useImperativeHandle, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { FormContext, FormContextType } from '@/components/form/FormContext.tsx';
import useFormValidation from '@core/hooks/useFormValidation.ts';
import { useTranslation } from 'react-i18next';

export interface FormContainerProps {
    children: React.ReactNode;
    value?: boolean;
    onChange?: (valid: boolean) => void;
    onSubmit?: (event: React.FormEvent) => void;
    className?: string;
    formRef?: React.Ref<FormContainerRef>;
}

export interface FormContainerRef {
    validate: () => boolean;
    resetValidation: () => void;
    reset: () => void;
    getErrors: () => Record<string, string>;
    getRequiredWarning: () => string;
}

const FormContainer = ({ children, value, onChange, onSubmit, className = '', formRef }: FormContainerProps) => {
    const { t } = useTranslation();
    const [formValid, setFormValid] = useState(false);
    const formElementRef = useRef<HTMLFormElement>(null);
    const onChangeRef = useRef(onChange);
    const prevValidRef = useRef(formValid);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    const validationInstance = useFormValidation();

    const isFormValid = Object.values(validationInstance.errors).every((error) => !error);

    useEffect(() => {
        if (isFormValid !== prevValidRef.current) {
            prevValidRef.current = isFormValid;
            setFormValid(isFormValid);
            onChangeRef.current?.(isFormValid);
        }
    }, [isFormValid]);

    // Stable imperative handle methods
    const validate = useCallback((): boolean => {
        return validationInstance.validateAll();
    }, [validationInstance]);

    const resetValidation = useCallback((): void => {
        validationInstance.reset();
    }, [validationInstance]);

    const reset = useCallback((): void => {
        resetValidation();
        formElementRef.current?.reset();
    }, [resetValidation]);

    const getErrors = useCallback((): Record<string, string> => {
        return validationInstance.getCurrentErrors();
    }, [validationInstance.getCurrentErrors]);

    const getRequiredWarning = useCallback((): string => {
        const errors = validationInstance.getCurrentErrors();
        const registeredLabels = validationInstance.getFieldLabels();
        const seen = new Set<string>();
        const labels: string[] = [];

        for (const [fieldName, error] of Object.entries(errors)) {
            if (!error) continue;
            const base = fieldName.replace(/[-_]\d+$/, '');
            if (seen.has(base)) continue;
            seen.add(base);
            labels.push(registeredLabels[fieldName] ?? base);
        }

        if (labels.length === 0) return '';
        if (labels.length === 1) return `${labels[0]} ${t('required')}`;
        return `${labels.join(', ')} ${t('areRequired')}`;
    }, [validationInstance.getCurrentErrors, validationInstance.getFieldLabels, t]);

    useImperativeHandle(
        formRef,
        () => ({
            validate,
            resetValidation,
            reset,
            getErrors,
            getRequiredWarning
        }),
        [validate, resetValidation, reset, getErrors, getRequiredWarning]
    );

    // Sync external value prop
    useEffect(() => {
        if (value !== undefined && value !== formValid) {
            setFormValid(value);
        }
    }, [value, formValid]);

    const handleSubmit = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            if (validate()) {
                onSubmit?.(event);
            }
        },
        [validate, onSubmit]
    );

    const contextValue: FormContextType = useMemo(() => {
        const {
            registerField,
            unregisterField,
            updateFieldValidity,
            updateFieldValue,
            handleChange,
            handleBlur,
            getFieldError,
            validateField,
            hasFieldError,
            validateAll,
            reset: validationReset,
            isValid,
            touched,
            attemptedSubmit,
            errors
        } = validationInstance;

        return {
            // Core methods
            registerField,
            unregisterField,
            updateFieldValidity,
            updateFieldValue,
            handleChange,
            handleBlur,
            getFieldError,
            validateField,

            // Compatibility methods
            getFieldErrors: () => errors,
            hasFieldError,
            validateAll,
            reset: validationReset,
            isValid,

            // State
            attemptedSubmit,
            touched
        };
    }, [
        validationInstance.registerField,
        validationInstance.unregisterField,
        validationInstance.updateFieldValidity,
        validationInstance.updateFieldValue,
        validationInstance.handleChange,
        validationInstance.handleBlur,
        validationInstance.getFieldError,
        validationInstance.validateField,
        validationInstance.hasFieldError,
        validationInstance.validateAll,
        validationInstance.reset,
        validationInstance.isValid,
        validationInstance.touched,
        validationInstance.attemptedSubmit,
        validationInstance.errors
    ]);

    return (
        <FormContext.Provider value={contextValue}>
            <form
                ref={formElementRef}
                className={`v-form ${className}`.trim()}
                onSubmit={handleSubmit}
                noValidate
                style={{ height: '100%' }}
            >
                {children}
            </form>
        </FormContext.Provider>
    );
};

FormContainer.displayName = 'FormContainer';

export default FormContainer;
